import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import L, { DivIcon } from "leaflet";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

interface TruckLocationDto {
  truckId: string;
  unitNumber?: string;
  latitude?: number;
  longitude?: number;
}

const fetchTruckLocation = async (
  token: string | null | undefined,
  unitNumber: string
) => {
  const { data } = await axios.get<TruckLocationDto>(
    `${apiVariable}/getTruckLocation/${unitNumber}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data;
};

const TruckViewControl = ({ truck }: { truck?: TruckLocationDto }) => {
  const map = useMap();

  React.useEffect(() => {
    if (truck?.latitude != null && truck.longitude != null) {
      map.setView([truck.latitude, truck.longitude], 12);
    }
  }, [map, truck]);

  return null;
};

const truckIcon = new DivIcon({
  html: `<div style="
      background:#1976d2;
      width:24px;height:24px;
      border-radius:50%;
      display:flex;
      align-items:center;
      justify-content:center;
      color:white;
      font-weight:bold;
      font-size:12px;
    ">T</div>`,
  className: "",
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

const OperationsFindMyTruck = () => {
  const { token } = useAuth();
  const [searchValue, setSearchValue] = useState("");
  const [unitNumber, setUnitNumber] = useState("");

  const {
    data: truckLocation,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["operations-truck-location", unitNumber],
    queryFn: () => fetchTruckLocation(token, unitNumber),
    enabled: !!token && !!unitNumber,
  });

  const handleSearch = () => {
    setUnitNumber(searchValue.trim());
  };

  return (
    <Card
      sx={{ maxWidth: 1200, mx: "auto", my: 4, boxShadow: 3, borderRadius: 2 }}
    >
      <CardContent sx={{ p: 0 }}>
        <Box
          sx={{
            p: 2,
            display: "flex",
            gap: 1,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <TextField
            fullWidth
            label="Truck's Unit Number"
            variant="outlined"
            size="small"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={!searchValue.trim()}
          >
            Find truck
          </Button>
          {isLoading && <CircularProgress size={24} />}
        </Box>

        <Box sx={{ px: 2, pb: 1 }}>
          {isError && (
            <Alert severity="error">{(error as Error)?.message}</Alert>
          )}
          {!unitNumber && (
            <Typography color="text.secondary">
              Enter a unit number to locate a truck on the map.
            </Typography>
          )}
        </Box>

        <Box
          sx={{ height: 800, borderRadius: "0 0 8px 8px", overflow: "hidden" }}
        >
          <MapContainer
            style={{ height: "100%", width: "100%" }}
            center={[39.8283, -98.5795]}
            zoom={4}
            scrollWheelZoom={false}
            doubleClickZoom={false}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="&copy; OpenStreetMap contributors"
            />

            {truckLocation?.latitude != null &&
              truckLocation.longitude != null && (
                <Marker
                  position={[truckLocation.latitude, truckLocation.longitude]}
                  icon={truckIcon}
                >
                  <Popup>
                    Truck: {truckLocation.unitNumber}
                    <br />
                    {truckLocation.latitude.toFixed(5)},{" "}
                    {truckLocation.longitude.toFixed(5)}
                  </Popup>
                </Marker>
              )}

            <TruckViewControl truck={truckLocation} />
          </MapContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

export default OperationsFindMyTruck;
